import api from 'api'
import { SignInModal, SignUpModal } from 'components/pages/auth'
import Router, { useRouter } from 'next/router'
import {
  ComponentType,
  createContext,
  FC,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react'

type AuthenticateBeforeExecutionArgs = {
  notificationText: string
  callback: () => void
}

const AuthContext = createContext<{
  user: User | undefined
  setUser?: (data: User) => void
  isAuthenticated: boolean
  loading: boolean
  setIsSignInOpened: (data: boolean) => void
  setIsSignUpOpened: (data: boolean) => void
  setSignInNotification: (data: string) => void
  setSignUpNotification: (data: string) => void
  authenticateBeforeExecution: ({
    notificationText,
    callback,
  }: AuthenticateBeforeExecutionArgs) => void
}>({
  user: undefined,
  isAuthenticated: false,
  loading: false,
  setIsSignInOpened: () => {},
  setIsSignUpOpened: () => {},
  setSignInNotification: () => {},
  setSignUpNotification: () => {},
  authenticateBeforeExecution: () => {},
})

const AuthProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User>()
  const [loading, setLoading] = useState(true)
  const [isSignInOpened, setIsSignInOpened] = useState(false)
  const [isSignUpOpened, setIsSignUpOpened] = useState(false)
  const [signInNotification, setSignInNotification] = useState('')
  const [signUpNotification, setSignUpNotification] = useState('')

  useEffect(() => {
    async function getUser() {
      const { data } = await api.get('/users/me')
      if (data) {
        setUser(data)
      }
      setLoading(false)
    }
    getUser()
  }, [])

  const handleSignUpClose = () => {
    setIsSignUpOpened(false)
  }

  const handleSignUp = () => {
    setIsSignInOpened(false)
    setIsSignUpOpened(true)
  }

  const handleSignIn = () => {
    setIsSignUpOpened(false)
    setIsSignInOpened(true)
  }

  const handleSignInClose = () => {
    setIsSignInOpened(false)
    setSignInNotification('')
  }

  const authenticateBeforeExecution = ({
    notificationText,
    callback,
  }: AuthenticateBeforeExecutionArgs) => {
    if (!user) {
      setSignInNotification(notificationText)
      setIsSignInOpened(true)
      return
    }

    callback()
  }

  if (loading) return null

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated: !!user,
        user,
        setUser,
        loading,
        setIsSignInOpened,
        setIsSignUpOpened,
        setSignInNotification,
        setSignUpNotification,
        authenticateBeforeExecution,
      }}
    >
      {children}

      <SignInModal
        notification={signInNotification}
        visible={isSignInOpened}
        onCancel={handleSignInClose}
        onSignUp={handleSignUp}
      />
      <SignUpModal
        notification={signUpNotification}
        visible={isSignUpOpened}
        onCancel={handleSignUpClose}
        onSignIn={handleSignIn}
      />
    </AuthContext.Provider>
  )
}

const useAuth = () => useContext(AuthContext)

function ProtectRoute(Component: ComponentType<any>) {
  // eslint-disable-next-line react/display-name
  return () => {
    const {
      user,
      isAuthenticated,
      loading,
      setIsSignInOpened,
      setIsSignUpOpened,
    } = useAuth()
    const router = useRouter()
    const { cid } = router.query as { cid?: string }

    // 認証周りが必要な画面のみ、２段階認証画面に遷移する
    // ProtectRouteが適用されていない画面の場合は、遷移しない
    useEffect(() => {
      if (user?.otp_required_for_login && !user?.two_factor_authenticated) {
        Router.push('/sign_in/two_factor')
      }
    }, [user])

    useEffect(() => {
      if (!isAuthenticated && !loading) {
        // 現在のURLを保存（クエリパラメータを含む）
        if (typeof window !== 'undefined') {
          localStorage.setItem(
            'originalUrl',
            window.location.pathname + window.location.search,
          )
        }

        if (cid) {
          setIsSignUpOpened(true)
        } else {
          setIsSignInOpened(true)
        }
      }
    }, [loading, isAuthenticated, setIsSignInOpened, setIsSignUpOpened, cid])

    if (!isAuthenticated) {
      return null
    }

    return <Component />
  }
}
function ProtectRouteWithout2faRoute(Component: ComponentType<any>) {
  // eslint-disable-next-line react/display-name
  return () => {
    const { isAuthenticated, loading, setIsSignInOpened } = useAuth()

    useEffect(() => {
      if (!isAuthenticated && !loading) {
        // 現在のURLを保存（クエリパラメータを含む）
        if (typeof window !== 'undefined') {
          localStorage.setItem(
            'originalUrl',
            window.location.pathname + window.location.search,
          )
        }
        setIsSignInOpened(true)
      }
    }, [loading, isAuthenticated, setIsSignInOpened])

    if (!isAuthenticated) {
      return null
    }

    return <Component />
  }
}

export { AuthProvider, ProtectRoute, ProtectRouteWithout2faRoute, useAuth }
